import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

interface UseConsignorIdResult {
  consignorId: string | null;
  loading: boolean;
}

/**
 * Resolves the consignorId (== consignors doc id == the consignor's auth uid) for
 * the current viewer, and redirects to '/' if none is found.
 *
 * `overrideId` (admin consignor-preview): when the VIEWER is staff (admin/auctioneer)
 * AND an overrideId is passed, the hook resolves THAT consignor instead of the
 * viewer's own uid — the rules already let isPaymentStaff() read any consignors/{cid}.
 * A non-staff caller's overrideId is IGNORED (falls back to own uid); the UI must not
 * even attempt someone else's data, and the rules would deny it regardless.
 */
export function useConsignorId(overrideId?: string): UseConsignorIdResult {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [consignorId, setConsignorId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) return;

    const isStaff = !!userProfile && ['admin', 'auctioneer'].includes(userProfile.role);
    const targetId = isStaff && overrideId ? overrideId : currentUser.uid;

    const lookup = async () => {
      try {
        // Direct get by doc id (== uid for the owner, or the overridden cid for staff).
        // The firestore rule permits it: request.auth.uid == cid (owner) OR isPaymentStaff().
        const snap = await getDoc(doc(db, 'consignors', targetId));
        if (!snap.exists()) {
          toast.error('Consignor account not found.');
          navigate('/');
          return;
        }
        setConsignorId(snap.id);
      } catch (err) {
        console.error('Error looking up consignor:', err);
        toast.error('Unable to load your account.');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    lookup();
  }, [currentUser, userProfile, overrideId, navigate]);

  return { consignorId, loading };
}
